import { useCallback } from 'react';
import { useRevitApi } from '../../../shared/api/revit/useRevitApi';
import { useBatchOperation } from '../../../shared/lib/batchOperations';
import type { RevitOperationProgressSnapshot } from '../../../shared/api/grpc/operationProgressClient';
import {
  applyE2EValidationQuickFix,
  USE_E2E_VALIDATION_MOCK,
} from '../lib/validationRuntimeEnv';

export interface ValidationQuickFixPayload {
  fixType: string;
  parameterName: string;
  revitParameterName: string;
  value: string;
  elementIds: string[];
  validationId: number;
}

export interface ValidationQuickFixExecutionOptions {
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}

function parseQuickFixResult(result: any): any {
  if (!result) {
    return result;
  }

  if (result.data) {
    return result.data;
  }

  if (typeof result.resultJson === 'string' && result.resultJson.length > 0) {
    return JSON.parse(result.resultJson);
  }

  return result;
}

export function useValidationQuickFixExecution() {
  const { call } = useRevitApi();
  const batchOperation = useBatchOperation();

  return useCallback(
    async (
      payload: ValidationQuickFixPayload,
      options: ValidationQuickFixExecutionOptions = {}
    ) => {
      return batchOperation.run({
        title: 'Validation quick fix',
        totalCount: payload.elementIds.length,
        run: async (helpers) => {
          helpers.updatePhase('queued', 'Quick fix queued for Revit.');
          if (USE_E2E_VALIDATION_MOCK) {
            return applyE2EValidationQuickFix(payload);
          }

          const queueResult = await call('validation.applyQuickFix', {
            fixDataJson: JSON.stringify(payload),
            onProgress: (progress: RevitOperationProgressSnapshot) => {
              options.onProgress?.(progress);
              const waiting =
                progress.state === 'waiting_for_revit' || progress.state === 'waiting_for_user_input';
              helpers.updatePhase(
                waiting ? 'polling' : 'running',
                progress.message || progress.waitReason || 'Running validation quick fix in Revit.'
              );
            },
          });

          if (!queueResult.success) {
            throw new Error(queueResult.error || 'Failed to queue quick fix');
          }

          if (queueResult.status === 'completed' || queueResult.data || queueResult.resultJson) {
            return parseQuickFixResult(queueResult);
          }

          const immediateResult = await call('validation.getQuickFixResult', {});
          if (immediateResult.status !== 'pending') {
            if (immediateResult.success) {
              return parseQuickFixResult(immediateResult);
            }

            throw new Error(immediateResult.error || 'Unknown error');
          }

          helpers.updatePhase('polling', 'Waiting for Revit to finish the quick fix.');
          const { result } = await helpers.pollUntil({
            intervalMs: 1000,
            maxAttempts: 60,
            poll: async () => call('validation.getQuickFixResult', {}),
            isPending: (result) => result.status === 'pending',
            timeoutMessage: 'Timeout: Quick fix took too long',
          });

          if (result.success) {
            return parseQuickFixResult(result);
          }

          throw new Error(result.error || 'Unknown error');
        },
        toSummary: (result: any) => ({
          total: Number(result?.totalCount ?? payload.elementIds.length),
          succeeded: Number(result?.fixedCount ?? 0),
          failed: Number(result?.failedCount ?? 0),
        }),
      });
    },
    [batchOperation, call]
  );
}
